import { Paper, Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import React, { forwardRef, useEffect, useMemo, useRef } from 'react'
import { TableVirtuoso } from 'react-virtuoso';
import { useAutoResizeHeight } from './hook';

const VirtualizedTable = (props) => {
    const {
        tableData = [],
        headerContent = () => <></>,
        rowContent = () => <></>,
        paperHeight = "100px",
        overscan = 200,
        tableStyle = {},
        tableContainerStyle = {},
        autoResize = true,
        matches = [],
        currentIndex = 0,
        id = ""
    } = props;
    const tableRef = useRef()
    const scrollerRef = useRef(null);
    const virtuosoRef = useRef();
    const matchSet = useMemo(() => {
        return new Set(matches.map(m => `${m.rowIndex}-${m.field}`));
    }, [matches]);
    useAutoResizeHeight(tableRef, 20, autoResize)
    useEffect(() => {
        if (!scrollerRef.current || !tableRef.current) return;
        const tableBody = scrollerRef.current.querySelector("tbody");
        const getRowPositions = () => {
            const rows = scrollerRef.current.querySelectorAll('tbody tr');
            if (rows.length > 0) {
                const lastRow = rows[rows.length - 1].getBoundingClientRect();
                const rect = tableRef.current.getBoundingClientRect();
                tableRef.current.style.height = `${lastRow.bottom - rect?.top + 2}px`;
            }
        };
        const observer = new MutationObserver(() => {
            getRowPositions()
        });

        const timeout = setTimeout(getRowPositions, 300);
        window.addEventListener("resize", getRowPositions);
        observer.observe(tableBody, { childList: true, subtree: true });
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('resize', getRowPositions);
        };
    }, [tableData]);
    useEffect(() => {
        if (!matches.length) return;

        const currentMatch = matches[currentIndex];
        if (!currentMatch) return;

        const { rowIndex, field, tableId } = currentMatch;

        if (tableId === id) {
            // 🔽 Vertical scroll
            virtuosoRef.current?.scrollToIndex({
                index: rowIndex,
                behavior: 'smooth',
                align: 'center',
            });

            // 🔁 Horizontal scroll
            requestAnimationFrame(() => {
                const row = scrollerRef.current?.querySelector(
                    `[data-row-index="${rowIndex}"]`
                );

                if (!row) return;

                const cell = Array.from(row.children).find(
                    (td) => td.getAttribute("data-field") === field
                );

                if (cell) {
                    cell.scrollIntoView({
                        behavior: 'smooth',
                        inline: 'center',
                        block: 'nearest',
                    });
                }
            });
        }
    }, [currentIndex, matches, tableData]);

    const VirtuosoTableComponents = useRef({
        Scroller: forwardRef((props, ref) => (
            <TableContainer
                className='scrollBlue'
                component={Paper}
                {...props}
                sx={{ boxShadow: 'none', ...tableContainerStyle, }}
                ref={(node) => {
                    scrollerRef.current = node;
                    if (typeof ref === 'function') ref(node);
                    else if (ref) ref.current = node;
                }}
            />
        )),
        Table: (props) => (
            <Table
                {...props}
                stickyHeader
                className="tableCommomSection"
                style={{ tableLayout: 'fixed', width: '100%', ...tableStyle }}
            />
        ),
        TableHead,
        TableRow: (props) => {
            const { item, ...rest } = props;
            return (
                <TableRow
                    hover
                    {...rest}
                    data-row-index={props['data-index']}
                />
            );
        },
        // TableRow: (props) => <TableRow hover {...props} />,
        TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
    }).current;

    return (
        <Paper
            ref={tableRef}
            sx={{
                height: paperHeight,

            }}
        >
            <TableVirtuoso
                ref={virtuosoRef}
                style={{ height: "100%" }}
                data={tableData?.length === 0 ? [{}] : tableData}
                components={VirtuosoTableComponents}
                fixedHeaderContent={headerContent}
                // itemContent={rowContent}
                itemContent={(index, row) =>
                    rowContent(index, row, {
                        matchSet,
                        currentMatch: matches[currentIndex]
                    })
                }
                overscan={overscan}
                computeItemKey={(index) => index}
                totalListHeightChanged={(height) => {
                    if (tableRef.current) {
                        tableRef.current.style.height = `${Math.max(height, 200)}px`
                    }
                }}

            />
        </Paper>
    )
}

export default VirtualizedTable